import type { ScaleLinear, ScaleTime } from 'd3-scale'

import type { AreaGraphElement } from '@/features/bidding/components/cascading/charts/types'

/** Radius of the hover marker circle */
const MARKER_RADIUS = 6

/** Stroke width for the marker circle */
const MARKER_STROKE_WIDTH = 1.5

/** Hover line color */
const HOVER_LINE_COLOR = '#9ca3af' // neutral-400

type GraphHoverLineProps = {
  timeScale: ScaleTime<number, number>
  valueScale: ScaleLinear<number, number>
  dataPoint: AreaGraphElement | null
  datetimes: Date[]
  selectedTimeIndex: number | null
  layerKeys: string[]
  height: number
  markerFill?: string
  /** Position marker at y=0 instead of data value (for diverging charts) */
  markerAtZero?: boolean
  /** Hide the circle marker, only show the vertical line */
  hideMarker?: boolean
}

/**
 * Vertical hover line with circle marker, centered on the bar.
 * For step charts, the line is positioned at the center of the time interval.
 */
function GraphHoverLine({
  timeScale,
  valueScale,
  dataPoint,
  datetimes,
  selectedTimeIndex,
  layerKeys,
  markerFill = '#2563eb',
  markerAtZero = false,
  hideMarker = false,
}: Readonly<GraphHoverLineProps>) {
  if (!dataPoint || selectedTimeIndex === null) return null

  // Calculate the interval to center the line on the bar
  const currentDatetime = datetimes[selectedTimeIndex]
  const nextDatetime = datetimes[selectedTimeIndex + 1]

  let x = timeScale(currentDatetime)

  // Center the line on the bar by adding half the interval
  if (nextDatetime) {
    const interval = timeScale(nextDatetime) - timeScale(currentDatetime)
    x += interval * 0.5
  } else if (datetimes.length >= 2) {
    // For the last point, estimate interval from previous points
    const estimatedInterval = timeScale(datetimes[1]) - timeScale(datetimes[0])
    x += estimatedInterval * 0.5
  }

  // Get the value for the marker - use y=0 for diverging charts, otherwise first layer's value
  const firstLayerKey = layerKeys[0]
  const getMarkerValue = (): number | undefined => {
    if (markerAtZero) return 0
    if (!firstLayerKey) return undefined
    return dataPoint.layerData[firstLayerKey]
  }
  const value = getMarkerValue()

  const showVerticalLine = Number.isFinite(x)
  const showMarker =
    !hideMarker && showVerticalLine && (markerAtZero || (value !== undefined && Number.isFinite(value)))

  const markerY = markerAtZero ? 0 : (value ?? 0)
  const y = showMarker ? valueScale(markerY) : 0
  const [y1, y2] = valueScale.range()

  return (
    <g className="hover-line" style={{ pointerEvents: 'none' }}>
      {/* Vertical line */}
      {showVerticalLine && (
        <line shapeRendering="crispEdges" stroke={HOVER_LINE_COLOR} strokeWidth={1} x1={x} x2={x} y1={y1} y2={y2} />
      )}
      {/* Circle marker */}
      {showMarker && (
        <circle
          cx={x}
          cy={y}
          fill={markerFill}
          r={MARKER_RADIUS}
          shapeRendering="geometricPrecision"
          stroke="white"
          strokeWidth={MARKER_STROKE_WIDTH}
        />
      )}
    </g>
  )
}

export default GraphHoverLine
